import React, { useState } from "react"

import DineroFactory from "dinero.js"
import Dinero from "dinero.js"

import {
  Amount,
  CartItemContainer,
  CartItemWrapper,
  DeleteButton,
  InfoWrapper,
  InputContainer,
  ItemDetails,
  ItemImage,
  ItemName,
  ItemPrice,
  Percentage,
  Prices
} from "./CartItem.styled"
import Money from "../../../../Money"
import {
  CompensationOrderLine,
  OrderCompensation,
  OrderLine
} from "lib/types/generated/graphql-types"
import { useMutation } from "@apollo/client"
import UPDATE_COMPENSATION from "../../../../../graphql/mutations/order/compensation/UpdateCompensation"
import {
  calculateTaxAmount,
  calculateVAT,
  compensationOrderLineOutputToInput,
  getCompensationInput
} from "../Helpers/helpers"
import Input from "../../../../Ui/Form/Input"
import PrimaryButton from "../../../../Ui/Buttons/PrimaryButton"
import { ReactComponent as ChevronDown } from "images/icons/chevron-down.svg"
import { ReactComponent as ChevronUp } from "images/icons/chevron-up.svg"
import { ReactComponent as DeleteIcon } from "images/icons/trash-can.svg"
import { handleErrorMessages } from "helpers/errors"
import { handleImageError } from "helpers/image"

type Props = {
  item: CompensationOrderLine
  currencyCode?: string
  orderLine: OrderLine | undefined
  compensation: OrderCompensation
  refetch: () => void
  isTaxIncludedInPrice: boolean
}

const CartItem = ({
  item,
  currencyCode,
  orderLine,
  compensation,
  refetch,
  isTaxIncludedInPrice,
  ...props
}: Props) => {
  const [amountInput, setAmountInput] = useState<number>(item?.totalDiscountAmount)
  const [amountExpanded, setAmountExpanded] = useState(false)

  const orderLines = compensation.orderLines ?? []

  const getTotalOrderLineAmount = () => orderLine?.distributedTotalPriceAmount

  const getTotalItemAmount = () => item?.totalAmount

  const getCompensationPercentage = () => {
    const percentage = (item?.totalDiscountAmount / (getTotalOrderLineAmount() ?? 0)) * 100
    return Number.isInteger(percentage) ? percentage : percentage.toFixed(1)
  }

  const [updateCompensation, { loading }] = useMutation(UPDATE_COMPENSATION, {
    onCompleted: () => {
      refetch()
      setAmountExpanded(false)
    },
    onError: (error) => {
      handleErrorMessages(error)
    }
  })

  const updateAmount = () => {
    const difference = amountInput - item?.totalDiscountAmount
    if (amountInput) {
      updateCompensation({
        variables: {
          compensationId: compensation.id,
          input: {
            ...getCompensationInput(compensation),
            payment: { amount: (compensation.payment?.amount ?? 0) + difference },
            orderLines: [
              ...orderLines.map((ol) => ({
                ...compensationOrderLineOutputToInput(ol),
                ...(ol?.orderLineId === item.orderLineId && {
                  totalAmount: ol?.totalAmount - difference,
                  totalDiscountAmount: amountInput,
                  totalTaxAmount: calculateVAT(
                    Dinero({
                      amount: amountInput,
                      currency: currencyCode as DineroFactory.Currency
                    }),
                    orderLine?.taxPercentage ?? 0,
                    orderLine?.taxPercentage ?? 0
                  ).getAmount()
                })
              }))
            ]
          }
        }
      })
    }
  }

  const removeOrderLine = () => {
    const compensationAmount = isTaxIncludedInPrice
      ? item?.totalDiscountAmount
      : item?.totalDiscountAmount +
        calculateTaxAmount(
          item?.totalDiscountAmount,
          orderLine?.taxPercentage ?? 0,
          orderLine?.taxPercentageDecimals ?? 0
        )
    updateCompensation({
      variables: {
        compensationId: compensation.id,
        input: {
          ...getCompensationInput(compensation),
          payment: { amount: (compensation.payment?.amount ?? 0) - compensationAmount },
          orderLines: [
            ...orderLines
              .filter((ol) => item.orderLineId !== ol.orderLineId)
              .map((ol) => compensationOrderLineOutputToInput(ol))
          ]
        }
      }
    })
  }

  return (
    <>
      <CartItemWrapper $loading={loading}>
        <CartItemContainer {...props}>
          <InfoWrapper>
            <ItemImage>
              <img src={orderLine?.imageUrl ?? ""} onError={handleImageError} />
            </ItemImage>
            <ItemDetails>
              <ItemName>{orderLine?.name}</ItemName>
              <Prices>
                <ItemPrice>
                  <div>New total:</div>
                  <Money
                    amount={getTotalItemAmount()}
                    currencyUnit={currencyCode as DineroFactory.Currency}
                  />
                </ItemPrice>
                <ItemPrice>
                  <div>Compensation:</div>-{" "}
                  <Money
                    amount={item?.totalDiscountAmount}
                    currencyUnit={currencyCode as DineroFactory.Currency}
                  />
                </ItemPrice>
              </Prices>
              <Percentage>
                {Number.isInteger(getCompensationPercentage())
                  ? getCompensationPercentage()
                  : `~${getCompensationPercentage()}`}
                %
              </Percentage>
            </ItemDetails>
          </InfoWrapper>
          <DeleteButton onClick={removeOrderLine}>
            <DeleteIcon />
          </DeleteButton>
        </CartItemContainer>
        {isTaxIncludedInPrice && (
          <Amount onClick={() => setAmountExpanded(!amountExpanded)}>
            Change amount {amountExpanded ? <ChevronUp /> : <ChevronDown />}
          </Amount>
        )}

        {amountExpanded && (
          <InputContainer>
            <div>
              <Input
                name={"compensationAmount"}
                label={"New compensation Amount"}
                type={"number"}
                defaultValue={item?.totalDiscountAmount}
                onChange={(e: React.FormEvent<HTMLInputElement>) =>
                  setAmountInput(e.currentTarget.value !== "" ? parseInt(e.currentTarget.value) : 0)
                }
              />
              {amountInput ? (
                <div>
                  <b>Formatted:</b>{" "}
                  <Money
                    amount={amountInput ?? 0}
                    currencyUnit={currencyCode as DineroFactory.Currency}
                  />
                </div>
              ) : (
                <div></div>
              )}
            </div>

            <PrimaryButton
              handleClick={() => updateAmount()}
              disabled={amountInput === item?.totalDiscountAmount}
              loading={loading}
            >
              Update
            </PrimaryButton>
          </InputContainer>
        )}
      </CartItemWrapper>
    </>
  )
}

export default CartItem
